// Web: plays a deity's transparent idle clips in random order, crossfading between two stacked
// <video> elements. No canvas, no per-frame JS; the browser just decodes small alpha videos.
import React, { useEffect, useRef } from 'react';
import { Asset } from 'expo-asset';
import type { HallClipSet } from './hallClips';

const FADE_S = 0.15;
const uriOf = (m: number) => Asset.fromModule(m).uri;
// WebKit (Safari, every iOS browser) only keys HEVC alpha; everything else gets VP9 alpha.
const useMov = typeof navigator !== 'undefined' && (/^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent) || /iP(hone|ad|od)/.test(navigator.userAgent));

// `active` is the native player's on/off; the web pauses off-screen figures itself (IntersectionObserver).
export function HallFigure({ set, width, height }: { set: HallClipSet; width: number; height: number; active?: boolean }) {
  const a = useRef<HTMLVideoElement | null>(null);
  const b = useRef<HTMLVideoElement | null>(null);
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const list = (useMov ? set.mov : set.webm).map(uriOf);
    const vids = [a.current, b.current];
    if (!vids[0] || !vids[1] || list.length === 0) return;
    let front = 0;
    let last = -1;
    let swapping = false;
    const pick = () => {
      if (list.length === 1) return 0;
      let n = Math.floor(Math.random() * list.length);
      if (n === last) n = (n + 1) % list.length;
      return n;
    };
    const load = (v: HTMLVideoElement) => {
      last = pick();
      v.src = list[last];
      v.load();
    };
    // Swap without a dip: the incoming clip sits fully opaque UNDER the outgoing one, and only the
    // top clip fades out (both frames are the same base pose). Fading both at once let the hall show
    // through mid-fade and read as a dark blink.
    const tick = () => {
      const f = vids[front]!;
      if (swapping || !f.duration || f.currentTime < f.duration - FADE_S - 0.05) return;
      const back = vids[1 - front]!;
      if (back.readyState < 2) return; // next clip not decoded yet: let this one hold its last frame
      swapping = true;
      back.style.transition = 'none';
      back.style.zIndex = '1';
      back.style.opacity = '1';
      back.currentTime = 0;
      void back.play().catch(() => {});
      f.style.zIndex = '2';
      f.style.transition = `opacity ${FADE_S}s linear`;
      f.style.opacity = '0';
      window.setTimeout(() => {
        f.pause();
        f.style.zIndex = '0';
        load(f); // queue the next random clip on the now-hidden element
        front = 1 - front;
        swapping = false;
      }, FADE_S * 1000 + 30);
    };
    // Start a random clip after a random delay so figures never move in lockstep.
    load(vids[0]);
    load(vids[1]);
    vids[0].style.opacity = '1';
    vids[0].style.zIndex = '1';
    vids[1].style.opacity = '0';
    vids[1].style.zIndex = '0';
    let visible = true;
    const t0 = window.setTimeout(() => { if (visible) void vids[front]!.play().catch(() => {}); }, Math.random() * 1500);
    // Only decode while on screen: off-screen figures pause (15 figures stay cheap on phones).
    const io = typeof IntersectionObserver !== 'undefined' && box.current
      ? new IntersectionObserver(([e]) => {
          visible = e.isIntersecting;
          if (visible) void vids[front]!.play().catch(() => {});
          else vids.forEach((v) => v!.pause());
        })
      : null;
    if (io && box.current) io.observe(box.current);
    vids.forEach((v) => { v!.addEventListener('timeupdate', tick); v!.addEventListener('ended', tick); });
    return () => {
      window.clearTimeout(t0);
      io?.disconnect();
      vids.forEach((v) => {
        v!.removeEventListener('timeupdate', tick);
        v!.removeEventListener('ended', tick);
        v!.pause();
        v!.removeAttribute('src');
        v!.load();
      });
    };
  }, [set]);

  const vstyle = {
    position: 'absolute', left: 0, top: 0, width, height, objectFit: 'contain',
    pointerEvents: 'none',
  } as const;
  const poster = uriOf(set.poster);
  if (set.webm.length === 0 && set.mov.length === 0) {
    // Still-only seat (no live loop yet): the poster alone.
    return <img src={poster} alt="" style={{ width, height, objectFit: 'contain', pointerEvents: 'none' }} />;
  }
  return (
    <div ref={box} style={{ position: 'relative', width, height }}>
      <video ref={a} muted playsInline preload="auto" poster={poster} style={vstyle} />
      <video ref={b} muted playsInline preload="auto" style={{ ...vstyle, opacity: 0 }} />
    </div>
  );
}
